"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Upload, Save, Loader, Image as ImageIcon } from "lucide-react";
export default function ArtworkDetailsForm() {
  return <section id="artworkdetailsform" className="w-full bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-2">
            Artwork Details Form
          </h2>
          <p className="text-muted-foreground text-lg">
            Archive and catalog pieces for NOBILITIES ANTIQUES GENERAL TRADING - L.L.C.
          </p>
        </div>

        <FormProvider formId="artwork_details_form_home" sectionName="Artwork Details Form" defaultValues={{
        artwork_image: "",
        title: "",
        artist: "",
        year_created: "",
        medium: "",
        dimensions: "",
        price: "",
        description: ""
      }} validationRules={{
        artwork_image: {
          required: "Artwork image is required"
        },
        title: {
          required: "Title is required",
          minLength: {
            value: 2,
            message: "Minimum 2 characters required"
          },
          maxLength: {
            value: 100,
            message: "Maximum 100 characters allowed"
          }
        },
        artist: {
          required: "Artist name is required",
          minLength: {
            value: 2,
            message: "Minimum 2 characters required"
          },
          maxLength: {
            value: 80,
            message: "Maximum 80 characters allowed"
          }
        },
        year_created: {
          min: {
            value: 1500,
            message: "Year must be 1500 or later"
          },
          max: {
            value: 2100,
            message: "Year must be 2100 or earlier"
          }
        },
        medium: {
          required: "Medium selection is required"
        },
        price: {
          min: {
            value: 0,
            message: "Price cannot be negative"
          }
        },
        description: {
          maxLength: {
            value: 1000,
            message: "Maximum 1000 characters allowed"
          }
        }
      }} submitText="Add to Vault" successMessage="Artwork successfully cataloged and added to the vault." errorMessage="There was an error saving the artwork details. Please check the fields and try again.">
          <InnerArtworkForm />
        </FormProvider>
      </div>
    </section>;
}
function InnerArtworkForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6 flex flex-col items-center text-center space-y-4">
          <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <Save className="size-6 text-primary" />
          </div>
          <h3 className="text-xl font-semibold text-foreground">Success!</h3>
          <p className="text-muted-foreground">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="mt-4">
            Catalog Another Piece
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl">Artwork Metadata</CardTitle>
        <CardDescription>Enter the detailed specifications and provenance of the piece.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit} className="space-y-8">
          
          {}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
              <AspectRatio ratio={16 / 9} className="flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-muted-foreground space-y-2 p-6 text-center">
                  <ImageIcon className="size-12 opacity-20" />
                  <p className="text-sm font-medium">Image Preview Area</p>
                  <p className="text-xs opacity-70 max-w-[250px]">
                    Upload a high-resolution image below to see the preview here.
                  </p>
                </div>
              </AspectRatio>
            </div>

            <FormField control={control} name="artwork_image" render={({
            field
          }) => <FormItem>
                  <FormLabel>Artwork Preview Image <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-4">
                      <Input type="file" accept="image/jpeg, image/png" className="cursor-pointer file:cursor-pointer file:bg-primary/10 file:text-primary file:hover:bg-primary/20 file:border-0 file:rounded-md file:px-4 file:py-1 file:mr-4" onChange={e => {
                  field.onChange(e.target.files ? e.target.files[0] : null);
                }} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Upload a high‑resolution image (JPEG, PNG). The current image will be displayed as a preview.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <Separator />

          {}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="title" render={({
            field
          }) => <FormItem>
                  <FormLabel>Title <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., "Sunset Over the Harbor"' {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="artist" render={({
            field
          }) => <FormItem>
                  <FormLabel>Artist Name <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Jane Doe" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="year_created" render={({
            field
          }) => <FormItem>
                  <FormLabel>Year Created</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="e.g., 2022" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormDescription>Enter the year the artwork was completed.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="medium" render={({
            field
          }) => <FormItem>
                  <FormLabel>Medium <span className="text-destructive">*</span></FormLabel>
                  <FormControl>
                    <div className="relative">
                      <select className="flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 appearance-none" {...field} value={field.value || ''}>
                        <option value="" disabled>Select medium</option>
                        {["Oil on Canvas", "Acrylic", "Watercolor", "Digital", "Mixed Media", "Photography", "Other"].map((option, i) => <option key={option} value={option} data-index={i}>
                            {option}
                          </option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="dimensions" render={({
            field
          }) => <FormItem>
                  <FormLabel>Dimensions (W × H × D in cm)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., 60 × 80 × 2" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormDescription>Provide width, height, and depth if applicable.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="price" render={({
            field
          }) => <FormItem>
                  <FormLabel>Price (USD)</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="e.g., 1500" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormDescription>Leave blank if the artwork is not for sale.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="description" render={({
          field
        }) => <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea placeholder="Add a brief description, story, or provenance..." className="min-h-[120px] resize-y" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2">
              <span className="mt-0.5"></span>
              <p>{submitError}</p>
            </div>}

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto min-w-[150px]">
              {isSubmitting ? <>
                  <Loader className="mr-2 size-4 animate-spin" />
                  Saving...
                </> : <>
                  <Save className="mr-2 size-4" />
                  Add to Vault
                </>}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}